/**
 * <sticky-atc> — barre d'achat qui apparaît quand le bouton d'ajout principal n'est
 * plus visible.
 *
 * Trois principes :
 * 1. La visibilité est pilotée par un IntersectionObserver sur le vrai bouton, pas par
 *    un seuil de défilement en pixels — ça reste juste quelle que soit la hauteur de la
 *    page, la taille d'écran ou le contenu injecté par une app.
 * 2. Le bouton de la barre ne poste rien lui-même : il clique le bouton réel. Toute la
 *    logique d'ajout au panier (bundle Moon, quantité, cadeaux, gestion d'erreur) reste
 *    donc celle de la page, et cette barre ne peut pas se désynchroniser d'elle.
 * 3. Ce qu'elle affiche est recopié du widget Moon Bundles, jamais recalculé. Le prix
 *    d'un pack est réutilisé tel quel, chaîne comprise : pas de conversion, donc pas de
 *    risque d'afficher un montant qui diffère de celui du widget. On ne fait une
 *    addition que lorsqu'un upsell est coché, et seulement si tous les montants en jeu
 *    sont lisibles — sinon on préfère afficher le prix du pack et nommer l'upsell
 *    plutôt qu'un total dont on n'est pas sûr.
 */
if (!customElements.get('sticky-atc')) {
  const MONEY = /\d[\d., \s]*/;

  const text = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');

  function parseMoney(value) {
    const match = String(value || '').match(MONEY);
    if (!match) return null;

    let digits = match[0].replace(/[\s ]/g, '');
    const lastSeparator = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
    // Un séparateur suivi d'au plus deux chiffres sépare les décimales ; au-delà il
    // sépare les milliers (« 1.254 » vaut mille deux cent cinquante-quatre).
    if (lastSeparator !== -1 && digits.length - lastSeparator - 1 <= 2) {
      digits = `${digits.slice(0, lastSeparator).replace(/[.,]/g, '')}.${digits.slice(lastSeparator + 1)}`;
    } else {
      digits = digits.replace(/[.,]/g, '');
    }

    const amount = parseFloat(digits);
    return Number.isFinite(amount) ? amount : null;
  }

  // Reformate un montant en copiant la mise en forme d'un prix existant : symbole,
  // position et séparateur décimal viennent du widget, pas de nos hypothèses.
  function formatLike(template, amount) {
    const match = String(template || '').match(MONEY);
    if (!match) return null;

    const decimal = match[0].trim().match(/([.,])\d{1,2}$/);
    const body = amount.toFixed(2).replace('.', decimal ? decimal[1] : ',');
    return template.replace(match[0], body);
  }

  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.realButton = document.querySelector('.product-form__submit');
        this.trigger = this.querySelector('[data-sticky-add]');
        if (!this.realButton || !this.trigger) return;

        this.titleEl = this.querySelector('[data-sticky-title]');
        this.metaEl = this.querySelector('[data-sticky-meta]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.fallback = {
          title: text(this.titleEl),
          price: this.priceEl ? this.priceEl.innerHTML : '',
        };

        this.trigger.addEventListener('click', () => {
          this.realButton.click();
          this.realButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.hidden = entry.isIntersecting;
            });
          },
          { rootMargin: '0px 0px -40px 0px' }
        );
        this.observer.observe(this.realButton);

        // Le bouton réel peut être désactivé (rupture de stock) : la barre doit suivre,
        // sinon elle propose un achat impossible.
        this.syncDisabled();
        this.mutation = new MutationObserver(() => this.syncDisabled());
        this.mutation.observe(this.realButton, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });

        this.watchOffer();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.mutation?.disconnect();
        this.offerMutation?.disconnect();
        if (this.onInteract) {
          document.removeEventListener('click', this.onInteract, true);
          document.removeEventListener('change', this.onInteract, true);
        }
      }

      syncDisabled() {
        const off = this.realButton.disabled || this.realButton.getAttribute('aria-disabled') === 'true';
        this.trigger.disabled = off;
      }

      /**
       * Moon Bundles est une app Svelte : changer de pack ou cocher un upsell re-rend
       * son arbre sans émettre d'événement qu'on puisse écouter.
       *
       * Observer sa racine ne suffit pas — elle est remplacée au premier re-rendu, et
       * l'observateur se retrouve attaché à un nœud détaché qui ne mutera plus jamais
       * (symptôme : la barre se fige sur le pack affiché au chargement). On écoute donc
       * les interactions à la source, et on garde une observation d'attributs à
       * l'échelle du document comme filet.
       */
      watchOffer() {
        let pending = null;
        const schedule = () => {
          clearTimeout(pending);
          pending = setTimeout(() => this.syncOffer(), 80);
        };

        // En phase de capture : l'app arrête parfois la propagation de ses propres clics.
        this.onInteract = (event) => {
          if (event.target?.closest?.('[class*="moonbundle"]')) schedule();
        };
        document.addEventListener('click', this.onInteract, true);
        document.addEventListener('change', this.onInteract, true);

        // La sélection active se matérialise par une classe. On filtre sur les nœuds du
        // widget pour ne pas re-synchroniser à chaque classe posée ailleurs sur la page.
        this.offerMutation = new MutationObserver((records) => {
          if (records.some((record) => record.target?.closest?.('[class*="moonbundle"]'))) schedule();
        });
        this.offerMutation.observe(document.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
        });

        // Le widget monte après nous : on repasse quelques fois pour attraper son état
        // initial sans attendre une première interaction.
        this.syncOffer();
        [300, 900, 2000].forEach((delay) => setTimeout(() => this.syncOffer(), delay));
      }

      readUpsells() {
        const inputs = [...document.querySelectorAll('[class*="moonbundle-upsell"] input[type="checkbox"]')];
        return inputs
          .filter((input) => input.checked)
          .map((input) => {
            const item = input.closest('[class*="upsell-item"]') || input.closest('[class*="upsell"]');
            if (!item) return { name: '', amount: null };

            const priceNode = [...item.querySelectorAll('*')]
              .reverse()
              .find((node) => !node.children.length && parseMoney(node.textContent) !== null);
            const titleNode = [...item.querySelectorAll('*')].find(
              (node) => !node.children.length && text(node) && parseMoney(node.textContent) === null
            );

            return { name: text(titleNode), amount: parseMoney(text(priceNode)) };
          });
      }

      syncOffer() {
        if (!this.titleEl || !this.priceEl) return;

        const active = document.querySelector('.moonbundle-option-active');
        if (!active) return;

        const title = text(active.querySelector('.moonbundle-option-title'));
        const priceText = text(active.querySelector('.moonbundle-option-single-price'));
        if (!title || !priceText) return;

        const upsells = this.readUpsells();
        const compare = text(active.querySelector('.moonbundle-option-old-price'));

        // La ligne secondaire sert à dire ce qu'on obtient en plus. Le sous-titre de
        // l'offre de base est un simple libellé de prix (« Regular price ») : l'afficher
        // occuperait une ligne pour ne rien apprendre. On ne le garde donc que s'il
        // annonce un ajout — Moon Bundles les préfixe d'un « + » — ou si l'offre est
        // remisée, auquel cas le sous-titre qualifie la remise.
        const subtitle = text(active.querySelector('.moonbundle-option-subtitle'));
        const parts = [];
        if (subtitle && (subtitle.startsWith('+') || compare)) parts.push(subtitle);
        const addonFallback = this.dataset.addonLabel || '';
        upsells.forEach((upsell) => parts.push(`+ ${upsell.name || addonFallback}`.trim()));

        let priceDisplay = priceText;
        let compareDisplay = compare;

        if (upsells.length) {
          const base = parseMoney(priceText);
          const summable = base !== null && upsells.every((upsell) => upsell.amount !== null);
          const total = summable
            ? formatLike(priceText, upsells.reduce((sum, upsell) => sum + upsell.amount, base))
            : null;

          if (total) {
            priceDisplay = total;
            // Le prix barré ne vaut que pour le pack seul : une fois l'upsell ajouté il
            // ne correspond plus à rien de comparable, mieux vaut ne rien barrer.
            compareDisplay = '';
          }
        }

        this.titleEl.textContent = title;
        this.render(priceDisplay, compareDisplay);

        if (this.metaEl) {
          this.metaEl.textContent = parts.join(' · ');
          this.metaEl.hidden = parts.length === 0;
        }
      }

      render(price, compare) {
        this.priceEl.textContent = price;
        if (!compare) return;

        const struck = document.createElement('s');
        struck.className = 'sticky-atc__compare';
        struck.textContent = compare;
        this.priceEl.append(struck);
      }
    }
  );
}
